import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  Button,
  Checkbox,
  Form,
  FormField,
  FormGroup,
  Icon,
  Input,
  Modal,
} from "semantic-ui-react";

import axios from "axios";
import { useIntl } from "react-intl";

import AutoResizeTextArea from "react-lib/apps/common/AutoResizeTextArea";

import MapPinLocation, { MapPinLocationRef } from "react-lib/apps/IsHealth/IHMobile/MapPinLocation";

import { OfficialAccountAddressDataType } from "./OfficialAccountAddressForm";

import CONFIG from "config/config";

type ModSearchLocationMapProps = {
  isCurrentLocation: boolean;
  open: boolean;
  masterData?: Partial<Record<string, any[]>>;
  onClose: () => void;
  onConfirm: ConfirmLocationHandler;
};

export type ConfirmLocationHandler = (
  data: Required<OfficialAccountAddressDataType>["division_address"]
) => void;

const myLocationStyle = {
  borderRadius: 0,
  bottom: "109px",
  height: "42px",
  right: "6px",
  width: "42px",
};

const ModSearchLocationMap: React.FC<ModSearchLocationMapProps> = (props) => {
  const intl = useIntl();
  const [latlng, setLatlng] = useState<string>("");
  const [placeDetail, setPlaceDetail] = useState<Record<string, any>>({});
  const [locationText, setLocationText] = useState<string>("");
  const [isCurrentLocation, setIsCurrentLocation] = useState<boolean>(props.isCurrentLocation);
  const [isSearchMode, setIsSearchMode] = useState<boolean>(false);
  const [textSearch, setTextSearch] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [typingTimeout, setTypingTimeout] = useState<any>(null);
  const [locations, setLocations] = useState<any[]>([]);

  const mapRef = useRef<MapPinLocationRef | null>(null);

  const handleSearchLocation = useCallback(
    async (search: string) => {
      if (search) {
        const response = await axios.get(
          `${CONFIG.PROXY_FUNCTION.GOOGLE_MAP_SEARCH_TEXT}/?query=${search}&language=${intl.locale}&location=15.8700,100.9925`
        );

        if (response.data?.result?.results?.length > 0) {
          setLocations(response.data?.result?.results);
        } else {
          setLocations([]);
        }

        setIsLoading(false);

        return;
      }

      setLocations([]);
      setIsLoading(false);
    },
    [textSearch]
  );

  useEffect(() => {
    if (isLoading) {
      void handleSearchLocation(textSearch);
    }
  }, [isLoading, textSearch]);

  useEffect(() => {
    if (!props.open) {
      resetState();
    }
  }, [props.open]);

  const handleChangeSearch = (value: any) => {
    setTextSearch(value);

    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    setTypingTimeout(
      setTimeout(() => {
        setIsLoading(true);
      }, 800)
    );
  };

  const inputIcon = () =>
    textSearch ? (
      <Icon
        name="close"
        link
        onClick={() => {
          handleChangeSearch("");
        }}
      />
    ) : (
      <Icon name="search" />
    );

  const resetState = () => {
    setLocationText("");
    setLatlng("");
    setPlaceDetail({});
    setIsCurrentLocation(false);
  };

  const parseGooglePlaceAddress = (addressComponents: any[]) => {
    const getComponent = (types: string[]) => {
      const component = addressComponents.find((comp) =>
        (comp.types as string[]).some((type) => types.includes(type))
      );

      return (component?.long_name || "") as string;
    };

    return {
      city: getComponent(["sublocality_level_2", "locality", "sublocality"]),
      district: getComponent(["administrative_area_level_2", "sublocality_level_1"]),
      province: getComponent(["administrative_area_level_1"]),
      zipcode: getComponent(["postal_code"]),
    };
  };

  const handlePinLocation = async (data: any) => {
    const params = data.place_id
      ? `place_id=${data.place_id}&language=${intl.locale}`
      : `latlng=${data.lat},${data.lng}&language=${intl.locale}`;

    try {
      const response = await axios.get(`${CONFIG.PROXY_FUNCTION.GOOGLE_MAP_GEOCODE}/?${params}`);

      const results: any[] = response.data?.result?.results ?? [];
      const result = results[0];

      if (!result) {
        return;
      }

      const formattedAddress = result.formatted_address;
      const parsedAddress = parseGooglePlaceAddress(result.address_components);

      setLocationText(formattedAddress);
      setLatlng(`${data.lat},${data.lng}`);

      setPlaceDetail({
        ...parsedAddress,
        name: data.place_name || "",
        place_id: data.place_id || "",
      });
    } catch (error) {
      console.error("Error fetching place details:", error);
    }
  };

  const isNameMatch = (item: { name: string; name_en: string }, searchText: string): boolean => {
    let text = (searchText || "").replaceAll(" ", "");

    if (text === "KrungThepMahaNakhon") {
      text = "Bangkok";
    }

    return (
      text.includes(item.name) ||
      text.includes(item.name_en) ||
      item.name.includes(text) ||
      item.name_en.includes(text)
    );
  };

  const handleConfirm = () => {
    if (!latlng || !locationText) {
      return;
    }

    const provinceItems: any[] = props.masterData?.province ?? [];
    const districtItems: any[] = props.masterData?.district ?? [];
    const cityItems: any[] = props.masterData?.city ?? [];

    const foundProvince = provinceItems.find((item) => isNameMatch(item, placeDetail.province));
    const foundDistrict = districtItems.find((item: any) =>
      isNameMatch(item, placeDetail.district)
    );
    const foundCity = cityItems.find((item: any) => isNameMatch(item, placeDetail.city));
    const zipcode = foundCity?.zipcode || placeDetail.zipcode || "";
    const [lat, lng] = latlng.split(",");

    props.onConfirm({
      city: foundCity?.id || "",
      district: foundDistrict?.id || "",
      full_address_text: locationText,
      province: foundProvince?.id || "",
      zipcode,
      latitude: lat,
      longitude: lng,
      place_id: placeDetail?.place_id,
    });

    props.onClose();
  };

  const handleUseCurrentLocation = (e: any, data: any) => {
    if (data.checked) {
      mapRef.current?.onGetMyLocation();
    }

    setIsCurrentLocation(data.checked);
  };

  const handleFocusInput = () => {
    if (!isSearchMode) {
      setIsSearchMode(true);
    }
  };

  const handleSelectAddress = (location: any) => {
    const locationLatlng = location.geometry.location;
    const address = {
      latitude: locationLatlng.lat,
      longitude: locationLatlng.lng,
      name: location.name,
      place_id: location.place_id,
    };

    setIsCurrentLocation(false);

    handleCloseSearchMode();

    mapRef.current?.getLocation(address);
  };

  const handleCloseSearchMode = () => {
    setTextSearch("");
    setIsSearchMode(false);
    setLocations([]);
  };

  return (
    <Modal
      style={{ minWidth: "90rem" }}
      open={props.open}
      size="large"
      closeOnDimmerClick
      onClose={props.onClose}
    >
      <div
        style={{
          display: "grid",
          gap: "1rem",
          gridTemplateColumns: "70% 30%",
          padding: "1.5rem 1rem",
        }}
      >
        <MapPinLocation
          ref={mapRef}
          debounceMs={500}
          height="calc(-15rem + 100vh)"
          myLocationStyle={myLocationStyle}
          readOnly={isCurrentLocation}
          zoomControl
          onPinLocation={handlePinLocation}
        />
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Form>
            <FormGroup inline>
              <FormField style={{ paddingRight: 0 }} width={15}>
                {isSearchMode && (
                  <Icon
                    style={{ cursor: "pointer", fontSize: "1.25rem", marginRight: "0.75rem" }}
                    name="chevron left"
                    onClick={handleCloseSearchMode}
                  />
                )}
                <Input
                  icon={inputIcon}
                  loading={isLoading}
                  placeholder="ค้นหาจากแผนที่"
                  value={textSearch}
                  fluid
                  onChange={(e) => {
                    handleChangeSearch(e.target.value);
                  }}
                  onClick={handleFocusInput}
                />
              </FormField>
            </FormGroup>
            {isSearchMode ? (
              <FormGroup>
                <FormField
                  style={{
                    height: "calc(-22.5rem + 100vh)",
                    overflow: "auto",
                    width: "96%",
                    ...(locations.length === 0 && {
                      alignItems: "center",
                      display: "flex",
                      justifyContent: "center",
                    }),
                  }}
                >
                  {locations.map((location, index) => (
                    <div
                      key={`location-${index}`}
                      style={{
                        borderBottom: "1px solid #E8E8E8",
                        cursor: "pointer",
                        padding: "0.75rem",
                        transition: "background-color 0.2s ease",
                      }}
                      onClick={() => handleSelectAddress(location)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#F5F5F5";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <div
                        style={{
                          color: "#1A1A1A",
                          fontSize: "0.95rem",
                          fontWeight: 500,
                          marginBottom: "0.25rem",
                        }}
                      >
                        {location.name}
                      </div>
                      <div
                        style={{
                          color: "#666666",
                          fontSize: "0.85rem",
                          lineHeight: "1.4",
                        }}
                      >
                        {location.formatted_address}
                      </div>
                    </div>
                  ))}

                  {locations.length === 0 && (
                    <div style={{ textAlign: "center" }}>
                      <img
                        style={{ width: "6rem" }}
                        src="/static/images/location-not-found.png"
                        alt="chat.icon"
                      />
                      <div
                        style={{
                          lineHeight: "1.5",
                          marginTop: "1rem",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {"ขออภัย ไม่พบที่อยู่นี้\nกรุณาค้นหาสถานใหม่อีกครั้ง"}
                      </div>
                    </div>
                  )}
                </FormField>
              </FormGroup>
            ) : (
              <>
                <FormGroup>
                  <FormField width={15}>
                    <Checkbox
                      checked={isCurrentLocation}
                      label="ใช้ตำแหน่งปัจจุบันของคุณ"
                      onChange={handleUseCurrentLocation}
                    />
                  </FormField>
                </FormGroup>
                <FormGroup style={{ marginTop: "2rem" }}>
                  <FormField width={15}>
                    <label style={{ fontWeight: "normal" }}>ยืนยันสถานที่ตั้ง</label>
                    <AutoResizeTextArea
                      rows={3}
                      value={locationText}
                      onChange={(value) => setLocationText(value)}
                    />
                  </FormField>
                </FormGroup>
              </>
            )}
          </Form>
          <div style={{ margin: "auto 0 0 auto" }}>
            <Button
              style={{
                backgroundColor: "rgba(217, 217, 217, 1)",
                color: "white",
                marginRight: "1rem",
              }}
              size="small"
              onClick={props.onClose}
            >
              ยกเลิก
            </Button>
            <Button
              style={{
                backgroundColor: "var(--primary-theme-color)",
                color: "white",
                marginRight: "1rem",
              }}
              size="small"
              onClick={handleConfirm}
            >
              ยืนยัน
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

ModSearchLocationMap.displayName = "ModSearchLocationMap";

export default React.memo(ModSearchLocationMap);
